/**
 * Assessment Run Page
 *
 * Allows users to take an assessment with written answers, through the existing
 * authority assessment API. The obsolete pseudo-live voice capture that used to
 * be mounted here (behind a compile-time flag that was false in every deployed
 * build) was removed with the rest of the legacy live-voice stack; the only live
 * conversation runtime is LiveConversationPanel.
 *
 * Route: /assessments/:objectiveId/run
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { UpgradePromptModal } from '../components/upgrade/UpgradePromptModal';
import { mutationOutcome } from '../utils/mutationOutcome';
import {
  useStartAssessmentMutation,
  useSubmitAnswerMutation,
  useFinalizeAssessmentMutation,
  useAbandonAssessmentMutation,
  useGetActiveSessionQuery,
  StartSessionResponse,
  FinalizeSessionResponse,
} from '../store/api/assessmentApi';

type PagePhase = 'loading' | 'ready' | 'answering' | 'submitting' | 'completed' | 'error';

export function AssessmentRunPage() {
  const { objectiveId } = useParams<{ objectiveId: string }>();
  const navigate = useNavigate();

  const [phase, setPhase] = useState<PagePhase>('loading');
  const [session, setSession] = useState<StartSessionResponse | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [turnNumber, setTurnNumber] = useState(1);
  const [finalResult, setFinalResult] = useState<FinalizeSessionResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Upgrade prompt state (local)
  const [upgradePrompt, setUpgradePrompt] = useState<{
    isOpen: boolean;
    reason: string;
    message: string;
    action: string;
  }>({ isOpen: false, reason: '', message: '', action: '' });
  const hideUpgradePrompt = () => setUpgradePrompt((prev) => ({ ...prev, isOpen: false }));

  // API mutations
  const [startAssessment, { isLoading: isStarting }] = useStartAssessmentMutation();
  const [submitAnswer, { isLoading: isSubmitting }] = useSubmitAnswerMutation();
  const [finalizeAssessment, { isLoading: isFinalizing }] = useFinalizeAssessmentMutation();
  const [abandonAssessment] = useAbandonAssessmentMutation();

  // Check for existing active session
  const { data: activeSessionCheck, isLoading: isCheckingActive } = useGetActiveSessionQuery(
    objectiveId || '',
    { skip: !objectiveId }
  );

  // Initialize session on mount
  useEffect(() => {
    if (!objectiveId) {
      setPhase('error');
      setErrorMessage('No objective ID provided');
      return;
    }

    if (isCheckingActive) return;

    const initSession = async () => {
      try {
        // Check if there's an active session to resume
        if (activeSessionCheck?.hasActiveSession && activeSessionCheck.session) {
          // Resume existing session
          const existingSession = activeSessionCheck.session;
          const lastTurn = existingSession.turns[existingSession.turns.length - 1];
          setSession({
            session: {
              id: existingSession.id,
              objectiveId: objectiveId,
              domainKey: null,
              categoryKey: null,
              difficulty: 'intermediate',
              status: existingSession.status,
              turnCount: existingSession.turnCount,
              templateId: null,
            },
            openingQuestion: lastTurn?.questionText || 'Resume your assessment.',
            expertPersona: null,
            maxTurns: 4,
          });
          setCurrentQuestion(lastTurn?.questionText || 'Resume your assessment.');
          setTurnNumber(existingSession.turnCount + 1);
          setPhase('ready');
          return;
        }

        // Start a new session
        const result = await startAssessment({ objectiveId }).unwrap();
        setSession(result);
        setCurrentQuestion(result.openingQuestion);
        setTurnNumber(1);
        setPhase('ready');
      } catch (error) {
        console.error('Failed to start assessment:', error);
        setPhase('error');
        setErrorMessage('Failed to start assessment. Please try again.');
      }
    };

    initSession();
  }, [objectiveId, isCheckingActive, activeSessionCheck, startAssessment]);

  // Handle answer submission
  const handleSubmitAnswer = async (text: string) => {
    if (!session || !text.trim()) return;

    setPhase('submitting');
    setAnswerText('');

    try {
      const result = await submitAnswer({
        sessionId: session.session.id,
        responseText: text,
      }).unwrap();

      if (result.shouldFinalize) {
        // Finalize the assessment
        const finalResult = await finalizeAssessment({
          sessionId: session.session.id,
        }).unwrap();
        setFinalResult(finalResult);
        setPhase('completed');
      } else if (result.nextQuestion) {
        // Continue with next question
        setCurrentQuestion(result.nextQuestion);
        setTurnNumber(result.currentTurn.turnNumber + 1);
        setPhase('ready');
      } else {
        // No more questions, finalize
        const finalResult = await finalizeAssessment({
          sessionId: session.session.id,
        }).unwrap();
        setFinalResult(finalResult);
        setPhase('completed');
      }
    } catch (error) {
      console.error('Failed to submit answer:', error);
      setPhase('error');
      setErrorMessage('Failed to submit answer. Please try again.');
    }
  };

  // Handle text submission
  const handleTextSubmit = () => {
    if (answerText.trim()) {
      handleSubmitAnswer(answerText);
    }
  };

  // Handle abandon
  const handleAbandon = async () => {
    if (session && confirm('Are you sure you want to abandon this assessment?')) {
      // Leave only once the server has actually abandoned it; otherwise the
      // assessment is still active and the user stays here, told why.
      const outcome = await mutationOutcome(
        abandonAssessment(session.session.id),
        'The assessment could not be abandoned. Please try again.',
        toast.error,
      );
      if (outcome.ok) navigate('/skills');
    }
  };

  // Loading state
  if (phase === 'loading' || isCheckingActive || isStarting) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-gray-600">Preparing assessment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (phase === 'error') {
    return (
      <div className="max-w-2xl mx-auto text-center py-16">
        <div className="text-red-500 text-5xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
        <p className="text-gray-600 mb-8">{errorMessage}</p>
        <div className="flex justify-center gap-4">
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
          >
            Try Again
          </button>
          <Link
            to="/skills"
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
          >
            Back to Skills
          </Link>
        </div>
      </div>
    );
  }

  // Completed state
  if (phase === 'completed' && finalResult) {
    const { session: resultSession, suggestions } = finalResult;
    const passed = resultSession.passed;

    // R0.2: `passed` is now THREE-valued. null means the assessment was completed
    // but not scored, because no evidence-based evaluator exists yet.
    //
    // The previous `passed ? ... : ...` ternaries fell through to the FALSE branch
    // on null, showing an orange "Keep Practicing!" card with a 💪 — telling the
    // user they had failed an assessment that was never judged. An unscored result
    // must imply neither success nor failure.
    const isUnscored = passed === null || passed === undefined;

    const headerTone = isUnscored
      ? 'border-gray-300 bg-gray-50'
      : passed
        ? 'border-green-300 bg-green-50'
        : 'border-orange-300 bg-orange-50';

    const headerIcon = isUnscored ? '✅' : passed ? '🎉' : '💪';
    const headerIconTone = isUnscored
      ? 'text-gray-400'
      : passed
        ? 'text-green-500'
        : 'text-orange-500';

    const headerTitle = isUnscored
      ? 'Practice completed'
      : passed
        ? 'Assessment Passed!'
        : 'Keep Practicing!';

    return (
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Result Header */}
        <div className={`card ${headerTone}`}>
          <div className="card-body text-center py-8">
            <div className={`text-6xl mb-4 ${headerIconTone}`}>{headerIcon}</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{headerTitle}</h1>
            {isUnscored ? (
              <p className="text-gray-600">
                Your responses have been recorded. Scoring isn't available yet,
                so this practice run has no score or result.
              </p>
            ) : (
              <p className="text-gray-600">{suggestions.message}</p>
            )}
          </div>
        </div>

        {/* Scores */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ScoreCard
            label="Knowledge"
            score={resultSession.knowledgeScore}
            color="blue"
          />
          <ScoreCard
            label="Delivery"
            score={resultSession.deliveryScore}
            color="purple"
          />
          <ScoreCard
            label="Authority"
            score={resultSession.authorityScore}
            color="green"
            isMain
            unavailableNote="Evidence-based scoring is not available yet"
          />
        </div>
        {isUnscored && (
          <p className="mt-3 text-xs text-gray-500 text-center">
            Scoring is not currently available for this assessment. This does not
            indicate a pass, a fail, or any particular level of knowledge or delivery.
          </p>
        )}

        {/* Feedback */}
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold text-gray-900">Feedback</h2>
          </div>
          <div className="card-body">
            <p className="text-gray-700 whitespace-pre-wrap">{resultSession.feedback}</p>
          </div>
        </div>

        {/* Suggestions */}
        {suggestions.suggestedObjectives.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2 className="font-semibold text-gray-900">Recommended Next Steps</h2>
            </div>
            <div className="card-body space-y-2">
              {suggestions.suggestedObjectives.map((obj) => (
                <Link
                  key={obj.id}
                  to={`/assessments/${obj.id}/run`}
                  className="block p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                >
                  <span className="text-gray-900">{obj.objectiveText}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-center gap-4">
          <Link
            to="/skills"
            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
          >
            Back to Skills
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
          >
            Take Again
          </button>
        </div>
      </div>
    );
  }

  // Active assessment state
  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Skill practice</h1>
          <p className="text-gray-500">
            Question {turnNumber} of {session?.maxTurns || 4}
            {session?.expertPersona && ` • with ${session.expertPersona.name}`}
          </p>
        </div>
        <button
          onClick={handleAbandon}
          className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors"
        >
          Abandon
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary-600 transition-all duration-300"
          style={{ width: `${(turnNumber / (session?.maxTurns || 4)) * 100}%` }}
        />
      </div>

      {/* Question Card */}
      <div className="card">
        <div className="card-header bg-gradient-to-r from-primary-500 to-indigo-600 text-white">
          <div className="flex items-center gap-3">
            {session?.expertPersona && (
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="font-bold">{session.expertPersona.name[0]}</span>
              </div>
            )}
            <div>
              <h3 className="font-semibold">
                {session?.expertPersona?.name || 'Expert Evaluator'}
              </h3>
              {session?.expertPersona?.title && (
                <p className="text-sm text-white/80">{session.expertPersona.title}</p>
              )}
            </div>
          </div>
        </div>
        <div className="card-body">
          <p className="text-lg text-gray-800 leading-relaxed">{currentQuestion}</p>
        </div>
      </div>

      {/* Submitting state */}
      {(phase === 'submitting' || isSubmitting || isFinalizing) && (
        <div className="card">
          <div className="card-body text-center py-8">
            <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto" />
            <p className="mt-4 text-gray-600">
              {isFinalizing ? 'Evaluating your responses...' : 'Processing your answer...'}
            </p>
          </div>
        </div>
      )}

      {/* Text Input */}
      {phase === 'ready' && (
        <div className="card">
          <div className="card-body">
            <textarea
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder="Type your answer here..."
              className="w-full h-40 resize-none rounded-lg border border-gray-300 px-4 py-3 text-gray-900 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 focus:outline-none"
            />
            <div className="flex justify-end mt-4">
              <button
                onClick={handleTextSubmit}
                disabled={!answerText.trim()}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  answerText.trim()
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Submit Answer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt Modal */}
      <UpgradePromptModal
        isOpen={upgradePrompt.isOpen}
        onClose={hideUpgradePrompt}
        reason={upgradePrompt.reason}
        message={upgradePrompt.message}
        action={upgradePrompt.action}
      />
    </div>
  );
}

// Score card component
/**
 * One assessment score tile.
 *
 * R0.2: `score` may be null — the Authority path no longer fabricates knowledge or
 * delivery numbers from response length. `Math.round(null)` is 0, so the previous
 * signature rendered an unmeasured assessment as a red-flag "0%".
 */
function ScoreCard({
  label,
  score,
  color,
  isMain = false,
  unavailableNote,
}: {
  label: string;
  score: number | null | undefined;
  color: 'blue' | 'purple' | 'green';
  isMain?: boolean;
  unavailableNote?: string;
}) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    green: 'bg-green-50 border-green-200 text-green-700',
  }[color];

  return (
    <div className={`card border ${colorClasses} ${isMain ? 'ring-2 ring-offset-2 ring-green-500' : ''}`}>
      <div className="card-body text-center py-6">
        {score === null || score === undefined ? (
          <div className="py-3">
            <div className="text-lg font-medium text-gray-400 italic">Not measured</div>
            {unavailableNote && (
              <p className="mt-1 text-xs text-gray-400">{unavailableNote}</p>
            )}
          </div>
        ) : (
          <div className={`text-4xl font-bold ${isMain ? 'text-green-600' : ''}`}>
            {Math.round(score)}%
          </div>
        )}
        <div className="text-sm mt-1">{label}</div>
        {isMain && score !== null && score !== undefined && (
          <div className="text-xs mt-1 font-medium">Overall Score</div>
        )}
      </div>
    </div>
  );
}

export default AssessmentRunPage;
